import { useState, useEffect } from "react";
import { apiClient } from "../App";
import { Card, CardContent } from "./ui/card";
import { TrendingUp, AlertTriangle, Shield, Users, Network } from "lucide-react";
import { OrganisationStructureTree } from "./OrganisationStructureTree";

const STAT = ({ label, value, hint, testid }) => (
  <Card className="border-slate-200"><CardContent className="p-4" data-testid={testid}>
    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
    <div className="text-3xl font-bold text-slate-900 mt-1">{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
  </CardContent></Card>
);

export function TrusteeDashboardSection() {
  const [tp, setTp] = useState(null);
  const [wf, setWf] = useState(null);
  const [ga, setGa] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await apiClient.get(`/workforce/dashboard/trustee-passport`);
        if (alive) setTp(r.data);
      } catch (e) { /* */ }
      try {
        const r = await apiClient.get(`/workforce/dashboard/trustee`);
        if (alive) setWf(r.data);
      } catch (e) { /* */ }
      try {
        const r = await apiClient.get(`/gift-aid/trustee-summary`);
        if (alive) setGa(r.data);
      } catch (e) { /* */ }
    })();
    return () => { alive = false; };
  }, []);

  if (!tp && !wf && !ga) {
    return <div className="text-slate-500 text-sm">Loading trustee metrics…</div>;
  }

  return (
    <div className="space-y-6" data-testid="trustee-dashboard">
      {/* Workforce Overview */}
      <div>
        <h3 className="font-heading font-bold mb-3 flex items-center gap-2">
          <Users className="h-5 w-5" /> Workforce Overview
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <STAT label="Total Fundraisers" value={tp?.totalFundraisers ?? 0}
            testid="td-total" />
          <STAT label="Ready" value={`${tp?.byStatusPct?.ready ?? 0}%`}
            hint={`${tp?.byStatus?.ready ?? 0} fundraisers`} testid="td-ready" />
          <STAT label="Action Required" value={`${tp?.byStatusPct?.action_required ?? 0}%`}
            hint={`${tp?.byStatus?.action_required ?? 0} fundraisers`}
            testid="td-action-required" />
          <STAT label="Avg Passport Score" value={tp?.avgPassportScore ?? 0}
            testid="td-avg-score" />
        </div>
      </div>

      {/* Compliance Risks */}
      <div>
        <h3 className="font-heading font-bold mb-3 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-600" /> Compliance Risks
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <STAT label="Expired DBS" value={tp?.topRisks?.dbsExpired ?? 0}
            testid="td-dbs-expired" />
          <STAT label="Expiring DBS" value={tp?.topRisks?.dbsExpiring ?? 0}
            testid="td-dbs-expiring" />
          <STAT label="Expired RTW" value={tp?.topRisks?.rtwExpired ?? 0}
            testid="td-rtw-expired" />
          <STAT label="Missing Agreements" value={tp?.topRisks?.missingAgreement ?? 0}
            testid="td-missing-agr" />
        </div>
      </div>

      {/* Deployment by Campaign */}
      {wf?.byCampaign && wf.byCampaign.length > 0 && (
        <div>
          <h3 className="font-heading font-bold mb-3 flex items-center gap-2">
            <Shield className="h-5 w-5" /> Deployment Readiness by Campaign
          </h3>
          <Card><CardContent className="p-4 overflow-x-auto">
            <table className="w-full text-sm" data-testid="td-campaign-table">
              <thead>
                <tr className="text-left text-xs uppercase text-slate-500 border-b">
                  <th className="py-2 pr-3">Campaign</th>
                  <th className="py-2 pr-3">Assigned</th>
                  <th className="py-2 pr-3">Ready</th>
                  <th className="py-2 pr-3">Conditional</th>
                  <th className="py-2 pr-3">Blocked</th>
                </tr>
              </thead>
              <tbody>
                {wf.byCampaign.map(c => (
                  <tr key={c.campaignId} className="border-b border-slate-100"
                    data-testid={`td-campaign-${c.campaignId}`}>
                    <td className="py-2 pr-3">{c.campaignName || c.campaignId}</td>
                    <td className="py-2 pr-3">{c.totalAssigned || 0}</td>
                    <td className="py-2 pr-3 text-emerald-700">{c.byDecision?.approved || 0}</td>
                    <td className="py-2 pr-3 text-amber-700">{c.byDecision?.conditionally_approved || 0}</td>
                    <td className="py-2 pr-3 text-rose-700">{c.byDecision?.blocked || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        </div>
      )}

      {/* Gift Aid Performance */}
      {ga && (
        <div>
          <h3 className="font-heading font-bold mb-3 flex items-center gap-2">
            <TrendingUp className="h-5 w-5" /> Gift Aid Performance
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <STAT label="Active Declarations" value={ga.activeDeclarations ?? 0}
              testid="td-ga-active" />
            <STAT label="Total Recoverable" value={`£${(ga.totalRecoverable ?? 0).toFixed(0)}`}
              testid="td-ga-recoverable" />
            <STAT label="Paid" value={`£${(ga.totalPaid ?? 0).toFixed(0)}`}
              testid="td-ga-paid" />
            <STAT label="Batches" value={ga.batchCount ?? 0}
              testid="td-ga-batches" />
          </div>
        </div>
      )}

      {/* OCI V2.5 — Workforce Hierarchy (read-only) */}
      <div data-testid="td-hierarchy-section">
        <h3 className="font-heading font-bold mb-3 flex items-center gap-2">
          <Network className="h-5 w-5" /> Workforce Hierarchy
        </h3>
        <OrganisationStructureTree canMutate={false} />
      </div>
    </div>
  );
}
